export interface CheatSection {
  title: string;
  items: { h: string; body: string }[];
}

/** Starter PF judging cheatsheet. Rules marked (verify) should be checked against the tournament's rules. */
export const PF_CHEATSHEET: CheatSection[] = [
  {
    title: 'Before the round',
    items: [
      { h: 'Coin flip', body: 'The winner picks side (Pro/Con) OR speaking order (first/second). The loser picks the other. Record it in the app so the flow is labeled correctly.' },
      { h: 'Tell them about you', body: 'Tell them your experience level and preferences: speed, whether you want an off-time roadmap, and whether you\'ll give time signals.' },
      { h: 'Timing', body: 'Say whether you\'re timing or they are. You\'re the official timer. Speakers may finish their sentence in grace, but not start new arguments.' },
    ],
  },
  {
    title: 'What each speech should do',
    items: [
      { h: 'Constructive (4:00)', body: 'Reads the case: contentions with evidence. Flow each contention on its own row: the claim, the key evidence (author + year), and the impact.' },
      { h: 'Crossfire 1 (3:00)', body: 'The first speakers question each other. Write down any admissions on the row they affect. Crossfire isn\'t a speech: it only counts if brought up later.' },
      { h: 'Rebuttal (4:00)', body: 'Attacks the opponent\'s case. The SECOND rebuttal should also frontline (defend its own case). Put each response on the row of the argument it answers.' },
      { h: 'Crossfire 2 (3:00)', body: 'The second speakers question each other.' },
      { h: 'Summary (3:00)', body: 'Narrows the round and extends the arguments the team is going for, including any defense and turns they need. Anything not in summary is out.' },
      { h: 'Grand crossfire (3:00)', body: 'All four debaters. It\'s often chaotic, so note only concessions that matter.' },
      { h: 'Final focus (2:00)', body: 'Explains why they win. It must match summary: nothing new. Weighing should be here.' },
    ],
  },
  {
    title: 'Deciding (in order)',
    items: [
      { h: '1. What\'s still alive?', body: 'Only arguments in BOTH summary and final focus of the team that made them. Check the flow for each argument.' },
      { h: '2. Who is winning each live argument?', body: 'Did the other side answer it? Was that answer extended? Were the answers frontlined? Turns are offense for the team that made them.' },
      { h: '3. Which one matters most?', body: 'Use the teams\' weighing (magnitude, probability, timeframe, scope, reversibility). If both weighed, decide whose weighing was better explained or went unanswered. If nobody weighed, use your best judgment and say so.' },
      { h: '4. Vote', body: 'The team winning the most important live argument usually wins. Commit before writing the RFD.' },
      { h: 'Stay on the flow', body: 'Don\'t vote on arguments you wish they had made, or on your own knowledge of the topic. If it wasn\'t said (and extended), it isn\'t in the round.' },
    ],
  },
  {
    title: 'Writing the ballot',
    items: [
      { h: 'RFD shape', body: '"I vote [side/team] because [argument] outweighs [their argument] on [weighing]. [What the losing team could have done]." Then add details.' },
      { h: 'Be specific and kind', body: 'Name the arguments. For each debater, give one thing they did well and one concrete thing to do next time. These are students.' },
      { h: 'Speaker points', body: 'Rough guide (verify with the tournament): 28.0–28.5 = solid, 29+ = excellent, below 27 = something went seriously wrong. Be consistent across rounds.' },
      { h: 'Low-point wins', body: 'Some tournaments don\'t allow the winner to have lower points (verify). If it happens, double-check that it reflects what you saw.' },
    ],
  },
  {
    title: 'Evidence and problems',
    items: [
      { h: 'Evidence requests', body: 'Teams may ask to see each other\'s evidence, usually during prep. Note who asked and for which card. You may ask to see evidence after the round if it matters to your decision.' },
      { h: 'Paraphrasing', body: 'Teams must have the original text available (verify tournament rules). If a paraphrase misstates the source, you may discount it.' },
      { h: 'Formal challenges', body: 'If a team accuses the other of fabricating or distorting evidence, stop the round and contact the tab room. Don\'t resolve it yourself.' },
      { h: 'Disclosure', body: 'Only disclose the decision orally if the tournament allows it. Submit on Tabroom first, then disclose.' },
    ],
  },
];

/** Lincoln-Douglas (CHSSA rules; NSDA uses the same times). */
export const LD_CHEATSHEET: CheatSection[] = [
  {
    title: 'Before the round',
    items: [
      { h: 'Sides', body: 'No coin flip: Aff and Neg are on the pairing. One debater a side. Aff speaks first and last.' },
      { h: 'Prep', body: '4 minutes per debater, used between speeches. You run both prep clocks.' },
      { h: 'Tell them about you', body: 'Say you\'re a newer judge if you are, and ask them to explain their framework clearly and not go too fast.' },
    ],
  },
  {
    title: 'What each speech should do',
    items: [
      { h: 'AC: Affirmative Constructive (6:00)', body: 'A value (the big goal, e.g. justice) and a criterion (how to measure it), then contentions that link to them. Flow the value and criterion on their own row at the top.' },
      { h: 'Cross-ex (3:00, Neg asks)', body: 'Questions only. Note admissions on the row they affect; they only matter if brought up in a speech.' },
      { h: 'NC: Negative Constructive (7:00)', body: 'The Neg\'s own value, criterion and contentions, plus attacks on the AC. Put each attack on the row of what it answers.' },
      { h: 'Cross-ex (3:00, Aff asks)', body: 'Aff questions the Neg.' },
      { h: '1AR: First Aff Rebuttal (4:00)', body: 'The hardest speech: it must answer the whole NC and defend the AC in 4 minutes. Short answers are normal. Anything it drops can be conceded.' },
      { h: 'NR: Negative Rebuttal (6:00)', body: 'The Neg\'s last speech: picks the winning arguments and weighs. Nothing brand new.' },
      { h: '2AR: Second Aff Rebuttal (3:00)', body: 'The last word: extends the Aff\'s best arguments and weighs. It may answer what the NR said, but not start new arguments.' },
    ],
  },
  {
    title: 'Deciding (in order)',
    items: [
      { h: '1. Framework first', body: 'Whose value and criterion did the round use? Often the debaters agree, or one criterion wins. That standard is how you judge everything else.' },
      { h: '2. What\'s still alive?', body: 'Only arguments carried into the side\'s last speech (2AR for Aff, NR for Neg).' },
      { h: '3. Who best meets the winning standard?', body: 'Weigh the live arguments under that framework. Say which argument decided it.' },
      { h: 'No plans', body: 'CHSSA LD is value debate: no plans or counterplans. If one appears, it\'s the other debater\'s job to object.' },
    ],
  },
];

/** Policy / CX (CHSSA rules; NSDA uses the same times). */
export const POLICY_CHEATSHEET: CheatSection[] = [
  {
    title: 'Before the round',
    items: [
      { h: 'Sides', body: 'No coin flip: Aff and Neg are on the pairing. Two debaters a side. Aff speaks first and last.' },
      { h: 'Prep', body: '8 minutes per team, used between speeches.' },
      { h: 'Speed', body: 'Policy can be very fast ("spreading"). Tell them your preference before the round. You may say "clear" or "slow" during a speech if you can\'t follow.' },
      { h: 'Flow by position', body: 'Policy flows usually get a sheet per position (the Aff case, each DA, CP, K, T). In the one-sheet layout, start each position on a new row and name it with the tag button.' },
    ],
  },
  {
    title: 'What each speech should do',
    items: [
      { h: '1AC (8:00)', body: 'The plan and why: harms (a problem), inherency (the status quo won\'t fix it), solvency (the plan does).' },
      { h: 'Cross-ex (3:00 after each constructive)', body: 'The other team asks questions. Note admissions on the row they affect.' },
      { h: '1NC (8:00)', body: 'Off-case positions (topicality, disadvantages, counterplans, kritiks) plus attacks on the case. Each position gets its own row or sheet.' },
      { h: '2AC (8:00)', body: 'Answers everything in the 1NC and rebuilds the case. Whatever it drops, the Neg can win.' },
      { h: '2NC + 1NR: the negative block (8:00 + 5:00)', body: 'Back to back, 13 minutes of Neg. They usually split the positions between the two speeches.' },
      { h: '1AR (5:00)', body: 'Answers the whole block in 5 minutes. Compressed answers are normal.' },
      { h: '2NR (5:00)', body: 'The Neg picks what to go for and drops the rest. Anything not in the 2NR is gone.' },
      { h: '2AR (5:00)', body: 'The last word: answers the 2NR and weighs. No new arguments.' },
    ],
  },
  {
    title: 'Deciding (in order)',
    items: [
      { h: '1. What\'s still alive?', body: 'Only what\'s in the 2NR and 2AR.' },
      { h: '2. What did the Neg go for?', body: 'A disadvantage that outweighs the case; a counterplan that solves the case and avoids a disadvantage (the "net benefit"); a kritik; topicality (the plan isn\'t within the resolution); or a case argument (e.g. no solvency).' },
      { h: '3. Compare', body: 'Does the Aff\'s case outweigh what the Neg is winning? Use the debaters\' weighing (magnitude, probability, timeframe).' },
      { h: 'New in the rebuttals', body: 'Rebuttals can\'t start new arguments. New answers in the 1AR to things first raised in the block are fine.' },
    ],
  },
];

/** Parliamentary (CHSSA rules, Article XI §4). */
export const PARLI_CHEATSHEET: CheatSection[] = [
  {
    title: 'Before the round',
    items: [
      { h: 'Topic and prep', body: 'The topic is announced 20 minutes before the round and changes every round. There is no prep time during the round: each speech starts right after the last.' },
      { h: 'Sides', body: 'Government and Opposition are on the pairing. Two debaters a side. Gov speaks first and last.' },
      { h: 'No computers', body: 'No electronics during the round. Quotes must have been copied by hand, with a citation, during prep. The other team may ask to see them.' },
    ],
  },
  {
    title: 'What each speech should do',
    items: [
      { h: 'PMC: Prime Minister Constructive (7:00)', body: 'Interprets the topic and presents the Government case.' },
      { h: 'LOC: Leader of Opposition Constructive (8:00)', body: 'Answers the Gov case. May challenge the interpretation and run its own arguments.' },
      { h: 'MGC: Member of Government Constructive (8:00)', body: 'Rebuilds the Gov case and answers the LOC.' },
      { h: 'MOC: Member of Opposition Constructive (8:00)', body: 'The last Opp constructive: new arguments are still allowed. The LOR follows right after it.' },
      { h: 'LOR: Leader of Opposition Rebuttal (4:00)', body: 'Opp\'s summary and weighing. No new arguments.' },
      { h: 'PMR: Prime Minister Rebuttal (5:00)', body: 'The last word. No new arguments, except it may answer new arguments from the MOC.' },
    ],
  },
  {
    title: 'Points of information and order',
    items: [
      { h: 'Points of information (POIs)', body: 'In constructives only, a debater may stand and offer a short question (15 seconds max). The speaker may accept or decline. Don\'t stop the clock. Not allowed in the first or last minute (protected time): the timer shows "Protected" or "POIs open".' },
      { h: 'Points of order', body: 'In rebuttals only, a debater may say "Point of order" if they think a new argument was made. Stop the clock; each side gets 15 seconds; restart the clock. Don\'t announce a ruling: just ignore the argument if it was new.' },
    ],
  },
  {
    title: 'Deciding',
    items: [
      { h: '1. Interpretation', body: 'If the teams disagreed about what the topic means, decide that first: it frames everything else.' },
      { h: '2. What\'s still alive?', body: 'Arguments carried into the side\'s rebuttal (LOR or PMR). Ignore new arguments in rebuttals.' },
      { h: '3. Weigh', body: 'Use the teams\' weighing. Parli rewards clear logic and explanation over evidence.' },
    ],
  },
];

export const CHEATSHEETS = { pf: PF_CHEATSHEET, ld: LD_CHEATSHEET, policy: POLICY_CHEATSHEET, parli: PARLI_CHEATSHEET } as const;

/** Quick tags for speaker notes: tap while they speak, use later for points and comments. Edit freely. */
export const SPEAKER_TAGS = {
  strengths: ['Clear', 'Organized', 'Good weighing', 'Strong evidence', 'Strong CX', 'Good extensions', 'Confident'],
  toWorkOn: ['Too fast', 'Hard to follow', 'Dropped args', 'Over time', 'No weighing', 'Rude in CX', 'Read too much'],
} as const;
